/// <amd-module name="Repos/VersionDropdown/Components/VersionDropdown" />
import { ArtifactDropdown, renderSelectedItem } from "Favorites/Dropdown/ArtifactDropdown";
import * as React from "react";
import { GitVersionType } from "Repos/Common/Contracts/Git";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import * as Resources from "Repos/VersionDropdown/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import {
    DropdownExpandableButton,
    DropdownExpandableTextField,
    IDropdownExpandableButtonProps,
    IDropdownExpandableTextFieldProps
} from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { getItemFromVersion, PivotIds, VersionDropdownProvider } from "../VersionDropdownProvider";
import { IVersionDropdownExpandableProps, IVersionDropdownItem, IVersionDropdownProps } from "./VersionDropdown.Props";

interface IVersionDropdownExpandableButtonProps extends IVersionDropdownExpandableProps, IDropdownExpandableButtonProps {
    initiallySelectedItem: IListBoxItem<IVersionDropdownItem> | undefined;
}

export const VersionDropdownExpandableButton = (props: IVersionDropdownExpandableButtonProps) => {
    const { initiallySelectedItem, placeholder, ...rest } = props;
    return (
        <DropdownExpandableButton {...rest} placeholder={props.initiallySelectedItem ? undefined : placeholder} subtle={true}>
            {props.initiallySelectedItem ? renderSelectedItem(props.initiallySelectedItem) : null}
        </DropdownExpandableButton>
    );
};

interface IVersionDropdownExpandableTextFieldProps extends IVersionDropdownExpandableProps, IDropdownExpandableTextFieldProps {
    initiallySelectedItem: IListBoxItem<IVersionDropdownItem> | undefined;
}

export const VersionDropdownExpandableTextField = (props: IVersionDropdownExpandableTextFieldProps) => {
    const { initiallySelectedItem, placeholder, ...rest } = props;
    return (
        <DropdownExpandableTextField
            {...rest}
            placeholder={props.initiallySelectedItem ? props.initiallySelectedItem.text : placeholder}
            prefixIconProps={props.initiallySelectedItem && props.selection.selectedCount === 0 ? props.initiallySelectedItem.iconProps : undefined}
            renderSelectedItems={(selection, items) => items[selection.value[0].beginIndex].text || ""}
        />
    );
};

export class VersionDropdown extends VssComponent<IVersionDropdownProps, {}> implements IFocusable<{}> {
    public static defaultProps = {
        renderExpandable: VersionDropdownExpandableButton
    };

    private provider: VersionDropdownProvider;
    private selection = new DropdownSelection();
    private hasMadeInitialSelection = false;
    private dropdown = React.createRef<ArtifactDropdown<IVersionDropdownItem>>();
    private selectedPivot = new ObservableValue<string>("");
    private searching = new ObservableValue<boolean>(false);
    private loading = new ObservableValue<boolean>(false);

    public constructor(props: IVersionDropdownProps, context: IVssComponentContext) {
        super(props, context);

        this.provider = new VersionDropdownProvider(
            context.pageContext,
            props.providerOptions || {},
            this.selection,
            this.searching,
            this.loading,
            props.renderAllBranchesPicker,
            this.props.onSelect
        );

        let initiallySelectedPivot: string = this.provider.getPivots()[0].id;
        if (props.initiallySelectedVersion) {
            if (props.initiallySelectedVersion.versionType === GitVersionType.Branch) {
                initiallySelectedPivot = PivotIds.branches;
            } else if (props.initiallySelectedVersion.versionType === GitVersionType.Commit) {
                initiallySelectedPivot = PivotIds.commits;
            } else if (props.initiallySelectedVersion.versionType === GitVersionType.Tag) {
                initiallySelectedPivot = PivotIds.tags;
            }
        }
        this.selectedPivot.value = initiallySelectedPivot;
    }

    public render(): JSX.Element | null {
        const {
            autoFocus,
            className,
            focusTreatment,
            onSelect,
            initiallySelectedVersion,
            isDropdownFullWidth,
            placeholder,
            providerOptions,
            renderExpandable
        } = this.props;

        return (
            <Observer
                selection={this.selection}
                items={{ observableValue: this.provider, filter: this.onItemsUpdated }}
                selectedPivot={{ observableValue: this.selectedPivot, filter: this.onSelectedPivotUpdated }}
            >
                {() => {
                    const value = this.selection.value.length
                        ? this.provider.value[this.selection.value[0].beginIndex].text
                        : this.props.initiallySelectedVersion?.version;

                    return (
                        <ArtifactDropdown<IVersionDropdownItem>
                            ref={this.dropdown}
                            calloutContentClassName={css(!isDropdownFullWidth && "version-dropdown-callout")}
                            className={css(className, "version-dropdown scroll-hidden")}
                            items={this.provider}
                            loading={this.loading}
                            onFilterTextChanged={this.onFilterTextChanged}
                            onSelect={onSelect}
                            pivots={this.provider.getPivots()}
                            placeholder={placeholder}
                            searching={this.searching}
                            selection={this.selection}
                            selectedPivot={this.selectedPivot}
                            showItemsWhileSearching={true}
                            renderExpandable={expandableProps => (
                                <Observer selection={this.selection}>
                                    {() => {
                                        const props = {
                                            ...expandableProps,
                                            autoFocus: autoFocus,
                                            focusTreatment: focusTreatment,
                                            initiallySelectedItem:
                                                this.selection.selectedCount === 0 && initiallySelectedVersion && !this.hasMadeInitialSelection
                                                    ? this.provider.getListItem(
                                                          getItemFromVersion(
                                                              initiallySelectedVersion,
                                                              !!providerOptions && !!providerOptions.repository
                                                                  ? providerOptions.repository
                                                                  : getGitRepository(this.context.pageContext)!
                                                          )
                                                      )
                                                    : undefined
                                        };
                                        return renderExpandable!(props);
                                    }}
                                </Observer>
                            )}
                            width={!isDropdownFullWidth ? 480 : undefined}
                        />
                    );
                }}
            </Observer>
        );
    }

    public resetSelection = () => {
        const selectedVersion = this.props.initiallySelectedVersion && this.props.initiallySelectedVersion.version;
        if (this.provider.value.length > 0 && selectedVersion) {
            // Select the item with the matching id
            for (let i = 0; i < this.provider.value.length; i++) {
                if (this.provider.value[i].data && this.provider.value[i].data!.versionDescriptor.version === selectedVersion) {
                    this.selection.select(i);
                    this.hasMadeInitialSelection = true;
                    return;
                }
            }
        }
    };

    public expand() {
        if (this.dropdown.current) {
            this.dropdown.current.expand();
        }
    }

    public focus() {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    public componentWillUnmount(): void {
        this.provider.dispose();
    }

    private onItemsUpdated = () => {
        if (!this.hasMadeInitialSelection) {
            this.resetSelection();
        }
        return false;
    };

    private onFilterTextChanged = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        // This function is called with a null event on callout close. Don't set loading when this happens
        this.provider.setSearchLoading(value, e !== null, this.selectedPivot.value);

        if (this.props.onFilterTextChanged) {
            this.props.onFilterTextChanged(e, value);
        }
    };

    private onSelectedPivotUpdated = () => {
        this.provider.updatePivot(this.selectedPivot.value);
    };
}
